import { Api } from '../utils/api';
import { DocumentUtils } from '../utils/document-utils';
import { mergePdfs } from '../utils/pdf-utils';
import { ShipmentStatus, ShipmentStatusMetadata } from './models/enums/shipment-status';
import { ShipmentDocumentType } from './models/shipments/shipment-document-types';
import { ShipmentSummary } from './models/shipments/shipment-summary';
import { ShipmentType } from './models/enums/shipment-type';
import { TransportationSvc } from './service';

/**
 * Something that is done for each of the shipments that were selected in a list, one after the other, as the activity
 * flyout shows.
 */
export interface ShipmentBulkActivity {

    /** What the flyout is headed with */
    title           : string;

    /** Whether the shipments are changed by it, which means the list is to be searched again once it is done */
    changesShipments: boolean;

    /** Why a shipment is passed over, when it is, as it is a shipment that the activity does not apply to */
    skipReason?     : (shipment : ShipmentSummary) => string | undefined;

    /** Does it for one shipment and resolves with a note for it, or throws the reason that it could not be done */
    process         : (shipment : ShipmentSummary) => Promise<string | void>;

    /**
     * Done once, after every shipment was gone through, when something was done for at least one of them.  It resolves
     * with a note for the whole of it, or throws the reason that it could not be done.
     */
    finish?         : () => Promise<string | void>;
}

/**
 * What stopped something from being done, in the words that were given with it.
 * @param err Whatever was thrown, an error of the API or one of the browser
 */
export const getFailureMessage = (err : any) : string => {
    return (err && err.message) ? err.message : 'An unknown error occurred.';
};

/**
 * Moves the shipments to the status.  The API makes the checks of the details page and says which of them a shipment
 * does not pass, and as the change is made against the row version of the list, a shipment that was changed by someone
 * since is refused.
 * @param service The transportation service
 * @param status The status that every one of the shipments moves to
 */
export const moveToStatusActivity = (service : TransportationSvc, status : ShipmentStatus) : ShipmentBulkActivity => {

    const display = ShipmentStatusMetadata[status].display;

    return {
        title           : `Move to ${ display }`,
        changesShipments: true,
        process         : async (shipment : ShipmentSummary) => {
            await service.moveShipmentToStatus(shipment, status);
            return `Moved to ${ display }`;
        }
    };
};

/**
 * Downloads a document for the shipments.  The PDFs are gathered as the shipments are gone through and downloaded as
 * the one file, in the order of the shipments, once they all are.  The spreadsheets are not put together, so each is a
 * file of its own.
 * @param document The document to download
 */
export const downloadDocumentActivity = (document : ShipmentDocumentType) : ShipmentBulkActivity => {

    // What was got for each shipment, which is kept across the shipments that are tried again
    const gathered = new Map<number, { index : number, report : Blob }>();

    // A shipment has its place from the first time that it is tried, so a retry does not move it
    const places = new Map<number, number>();
    let combined = false;
    let kind = '';

    return {
        title           : `Download ${ document.display }`,
        changesShipments: false,
        skipReason      : (shipment : ShipmentSummary) => document.unavailable(shipment.status),
        process         : async (shipment : ShipmentSummary) => {

            if (!places.has(shipment.id)) {
                places.set(shipment.id, places.size);
            }

            const req = document.create(shipment.id, shipment.type);
            const report = await Api.GetBlob(req.url);
            if (!report || report.size === 0) {
                throw new Error("We're unable to find that document!");
            }

            if (req.fileExtension === 'pdf') {
                combined = true;
                kind = shipment.type === ShipmentType.Truck ? 'Trucks' : 'Containers';
                gathered.set(shipment.id, { index: places.get(shipment.id)!, report });
                return 'Added to the combined PDF';
            }

            DocumentUtils.openDoc(report, req.filename, req.openInTab, req.fileExtension);
            return req.filename;
        },
        finish          : async () => {

            if (!combined || gathered.size === 0) {
                return;
            }

            const reports = Array.from(gathered.values()).sort((a, b) => a.index - b.index).map(x => x.report);
            const filename = `${ kind }_${ document.display.replace(/[^A-Za-z0-9]/g, '') }`;

            DocumentUtils.openDoc(await mergePdfs(reports), filename, false, 'pdf');
            return `${ filename }.pdf`;
        }
    };
};
